import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getImages } from './api';
import { useNavigate } from 'react-router-dom';
import Spinner from './ui/Spinner';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import Alert from './ui/Alert';
import Badge from './ui/Badge';
import { useToast } from './Toast';
import UploadPanel from './media/UploadPanel';
import { mediaLabel, mediaState, pageWindow, tagChips } from './mediaHelpers';

// Tag chips shown before "Show all".
const TAG_CHIP_LIMIT = 24;

const Images = () => {
  const [images, setImages] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(18);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [tagCounts, setTagCounts] = useState({});
  const [searchMode, setSearchMode] = useState('keyword');
  const [showAllTags, setShowAllTags] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSearchQuery, setCurrentSearchQuery] = useState(''); // Track the actual search being performed
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [activeTags, setActiveTags] = useState([]);
  const [editorialOnly, setEditorialOnly] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToast();

  useEffect(() => {
    // Ignore a response that lands after the filters have changed again.
    let stale = false;
    const fetchImages = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getImages(page, perPage, currentSearchQuery, {
          tags: activeTags,
          editorialOnly,
        });
        if (stale) return;
        setImages(data.media_list);
        setTotal(data.total);
        setTotalPages(Math.max(1, data.total_pages || Math.ceil(data.total / data.per_page) || 1));
        setTagCounts(data.tag_counts || {});
        setSearchMode(data.search_mode || 'keyword');
        setPage(data.page);
        setPerPage(data.per_page);
      } catch (err) {
        if (stale) return;
        if (err.response && err.response.status === 401) {
          navigate('/login');
        } else {
          setError(err.response?.data?.detail || 'Failed to fetch images');
        }
      } finally {
        if (!stale) setLoading(false);
      }
    };

    fetchImages();
    return () => {
      stale = true;
    };
  }, [page, perPage, currentSearchQuery, activeTags, editorialOnly, reloadToken, navigate]);

  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1); // Reset to first page on new search
    setCurrentSearchQuery(searchQuery); // This triggers the search
  };

  const handleClear = () => {
    setSearchQuery('');
    setCurrentSearchQuery('');
    setActiveTags([]);
    setEditorialOnly(false);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Tags and the editorial flag filter the whole library on the server, so
  // any change starts again from page 1.
  const toggleTag = (tag) => {
    setActiveTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
    setPage(1);
  };

  const toggleEditorial = () => {
    setEditorialOnly((v) => !v);
    setPage(1);
  };

  // Chips come from the tag facet over the whole result set, not this page.
  const { chips: tagOptions, hidden: hiddenTags } = useMemo(
    () => tagChips(tagCounts, activeTags, showAllTags ? Infinity : TAG_CHIP_LIMIT),
    [tagCounts, activeTags, showAllTags]
  );
  const filtered = activeTags.length > 0 || editorialOnly;

  const handleUploadFinished = ({ uploaded, failed }) => {
    if (uploaded) {
      addToast(
        `${uploaded} image${uploaded === 1 ? '' : 's'} uploaded — thumbnails follow in a few seconds`,
        'success'
      );
      setShowUpload(false);
      refresh();
    }
    if (failed) {
      addToast(`${failed} upload${failed === 1 ? '' : 's'} failed`, 'error');
    }
  };

  const isLastPage = page >= totalPages;

  const pagination = (
    <nav aria-label="Media pages" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => handlePageChange(page - 1)}
        disabled={page === 1}
      >
        Previous
      </button>
      {pageWindow(page, totalPages).map((p, i) =>
        p === '…' ? (
          <span key={`gap-${i}`} className="muted small">…</span>
        ) : (
          <button
            key={p}
            type="button"
            className={p === page ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
            aria-current={p === page ? 'page' : undefined}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => handlePageChange(page + 1)}
        disabled={isLastPage}
      >
        Next
      </button>
    </nav>
  );

  return (
    <div className="page">
      <PageHeader
        title="Media"
        subtitle="Images and files available to every site"
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setShowUpload(true)}>
            Upload images
          </button>
        }
      />

      {/* Search form */}
      <form onSubmit={handleSearch} className="toolbar">
        <input
          type="search"
          className="input grow"
          aria-label="Search media"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search by name, filename, or describe what's in the image..."
        />
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Searching…' : 'Search'}
        </button>
        <button
          type="button"
          className={editorialOnly ? 'chip active' : 'chip'}
          aria-pressed={editorialOnly}
          onClick={toggleEditorial}
        >
          Editorial only
        </button>
        {(searchQuery || currentSearchQuery || activeTags.length || editorialOnly) ? (
          <button type="button" className="btn btn-ghost" onClick={handleClear}>
            Clear
          </button>
        ) : null}
      </form>

      {tagOptions.length > 0 && (
        <div className="chip-group" style={{ marginBottom: '16px' }}>
          {tagOptions.map(({ tag, count }) => (
            <button
              key={tag}
              type="button"
              className={activeTags.includes(tag) ? 'chip active' : 'chip'}
              aria-pressed={activeTags.includes(tag)}
              onClick={() => toggleTag(tag)}
            >
              {tag} <span className="count">{count}</span>
            </button>
          ))}
          {(hiddenTags > 0 || showAllTags) && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowAllTags((v) => !v)}>
              {showAllTags ? 'Show fewer tags' : `Show all tags (+${hiddenTags})`}
            </button>
          )}
        </div>
      )}

      {/* Results info with top pagination */}
      {!loading && images.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
          <p className="muted" style={{ margin: 0 }}>
            {currentSearchQuery
              ? `Found ${total.toLocaleString()} result${total === 1 ? '' : 's'} for "${currentSearchQuery}"`
              : `${total.toLocaleString()} media item${total === 1 ? '' : 's'}${filtered ? ' match the filters' : ''}`}
            {currentSearchQuery && searchMode === 'hybrid' ? ' (including visual matches)' : ''}
            {` · Page ${page} of ${totalPages.toLocaleString()}`}
          </p>
          {pagination}
        </div>
      )}

      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>

      {loading ? (
        <Spinner label="Loading media…" />
      ) : images.length > 0 ? (
        <div className="card-grid">
          {images.map((image) => {
            const processing = mediaState(image) === 'processing';
            return (
              <div
                key={image._id}
                className="card"
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/images/${image._id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/images/${image._id}`);
                  }
                }}
                style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }}
              >
                <div style={{
                  width: '100%',
                  height: '180px',
                  backgroundColor: 'var(--color-bg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}>
                  {!processing && (
                    <img
                      src={image.thumb_url}
                      alt={mediaLabel(image)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0, zIndex: 2 }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="muted small" style={{ zIndex: 1 }}>
                    {processing ? 'Generating thumbnail…' : 'Image not available'}
                  </div>
                  {processing && (
                    <div style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 3 }}>
                      <Badge state="warn">Processing</Badge>
                    </div>
                  )}
                  {image.editorial_only && (
                    <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 3 }}>
                      <Badge state="info">Editorial</Badge>
                    </div>
                  )}
                </div>
                <div style={{ padding: '10px 12px' }}>
                  <div style={{
                    fontWeight: 600,
                    marginBottom: '4px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.3,
                  }}>{mediaLabel(image)}</div>
                  {image.tags && image.tags.length > 0 && (
                    <div className="chip-group" style={{ marginTop: '6px' }}>
                      {image.tags.slice(0, 4).map((tag) => (
                        <span key={tag} className="badge badge-plain">{tag}</span>
                      ))}
                      {image.tags.length > 4 && (
                        <span className="muted small">+{image.tags.length - 4}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title={
            error
              ? 'Could not load media'
              : currentSearchQuery
                ? 'No media matches your search'
                : filtered
                  ? 'No media matches the filters'
                  : 'No media yet'
          }
          message={
            error
              ? 'Retry the search or reload the page.'
              : filtered
                ? 'Clear a tag or the editorial filter to see more.'
                : currentSearchQuery
                  ? 'Try different words, or describe what is in the image.'
                  : 'Upload an image to start the library.'
          }
          action={
            currentSearchQuery || activeTags.length || editorialOnly ? (
              <button type="button" className="btn btn-secondary" onClick={handleClear}>
                Clear filters
              </button>
            ) : (
              <button type="button" className="btn btn-primary" onClick={() => setShowUpload(true)}>
                Upload images
              </button>
            )
          }
        />
      )}

      {!loading && images.length > 0 && totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
          {pagination}
        </div>
      )}

      {showUpload && (
        <div className="modal-backdrop" onClick={() => setShowUpload(false)}>
          <div
            className="modal modal-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="upload-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="upload-modal-title">Upload images</h3>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowUpload(false)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <UploadPanel autoFocus onFinished={handleUploadFinished} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Images;
